import type { Product, ProductLeaf } from "@decocms/apps-commerce/types";
import { Picture } from "~/components/ui/Picture";
import { useEffect, useRef, useState } from "react";
import { getProductImageAlt, getSelectedColor } from "~/utils/product";
import { sanitizeStoreUrl } from "~/utils/constants";
import DiscountBadge from "~/components/ui/DiscountBadge";
import Slider from "~/components/ui/Slider";
import VariantSelectorShelf from "~/components/product/VariantSelectorShelf";
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice";
import { useOffer } from "~/sdk/useOffer";
import { useSkuSelector } from "~/sdk/useSkuSelector";

interface Props {
  product: Product;
  index: number;
}

const ProductItemCategories = ({ product, index }: Props) => {
  const { selectedSku } = useSkuSelector();

  const [currentSku, setCurrentSku] = useState<string | null>(
    selectedSku.value,
  );
  const [selectedProduct, setSelectedProduct] = useState<ProductLeaf | null>(
    null,
  );
  const [selectedProductImage, setSelectedProductImage] = useState<
    ProductLeaf | null
  >(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  const [isCardVisible, setIsCardVisible] = useState(false);

  const variants = product.isVariantOf?.hasVariant ?? [];
  const availableVariant = variants?.find((variant) =>
    (variant.offers?.offers?.[0]?.inventoryLevel?.value ?? 0) > 0
  );
  // Vitrines não trazem inventoryLevel por variante (só o PDP traz), então
  // availableVariant quase sempre fica undefined aqui — sem esse fallback pro
  // produto "raiz", a imagem simplesmente não renderiza (nome/preço vêm de
  // `product` direto e continuam aparecendo, mascarando o bug).
  const image = selectedProductImage?.image?.length
    ? selectedProductImage.image
    : availableVariant?.image?.length
    ? availableVariant.image
    : product.image;
  const primaryImageUrl = image?.[0]?.url ?? image?.[1]?.url ?? "";

  useEffect(() => {
    setSelectedProduct(availableVariant ?? null);

    setSelectedProductImage(
      variants.find((variant) => variant.sku === currentSku) ?? null,
    );
  }, [currentSku, availableVariant, variants]);

  useEffect(() => {
    setIsImageLoaded(false);
  }, [primaryImageUrl]);

  useEffect(() => {
    const imageElement = imageContainerRef.current?.querySelector("img");

    if (!imageElement || !primaryImageUrl) {
      return;
    }

    if (imageElement.complete && imageElement.naturalWidth > 0) {
      setIsImageLoaded(true);
    }
  }, [primaryImageUrl]);

  useEffect(() => {
    const node = imageContainerRef.current;
    if (!node || isCardVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsCardVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [isCardVisible]);

  const productName = product.isVariantOf?.name;
  const { offers } = selectedProduct || product;
  const {
    price = 0,
    listPrice = 0,
    seller = "1",
    installments,
  } = useOffer(offers);

  const selectedColor = getSelectedColor(
    selectedProductImage || availableVariant,
  );

  return (
    <Slider.Item
      index={index}
      className="carousel-item first:pl-0 last:pr-0 w-[65%] md:w-[23.5%] flex justify-center h-auto lg:h-fit max-h-max"
    >
      <div className="group flex flex-col items-center w-full h-auto max-h-max lg:h-[641px] lg:justify-between">
        <div className="relative w-full">
          <a
            href={sanitizeStoreUrl(availableVariant?.url || product.url)}
            tabIndex={-1}
            className="group flex flex-col items-center w-full"
          >
            <div className="absolute left-2 top-1 lg:left-10 lg:top-5 z-10">
              <DiscountBadge listPrice={listPrice} price={price} />
            </div>
            {image && (
              <div
                ref={imageContainerRef}
                className="relative w-55 lg:w-[424px] max-w-full aspect-square overflow-hidden"
              >
                {!isImageLoaded && (
                  <div className="absolute inset-0 animate-pulse bg-[#F5F5F5]" />
                )}
                <Picture
                  sources={[
                    {
                      media: "(max-width: 1023px)",
                      src: primaryImageUrl,
                      width: 220,
                      height: 220,
                    },
                    {
                      media: "(min-width: 1024px)",
                      src: primaryImageUrl,
                      width: 424,
                      height: 424,
                    },
                  ]}
                  loading="lazy"
                  fetchPriority="low"
                  decoding="async"
                  className={`lg:group-hover:hidden w-full h-full object-cover transition-opacity duration-200 ${
                    isImageLoaded ? "opacity-100" : "opacity-0"
                  }`}
                  src={primaryImageUrl}
                  alt={getProductImageAlt(productName, 0, selectedColor)}
                  width={220}
                  height={220}
                  onLoad={() => setIsImageLoaded(true)}
                  onError={() => setIsImageLoaded(true)}
                />
                {isCardVisible && (
                  <Picture
                    sources={[
                      {
                        media: "(max-width: 1023px)",
                        src: image[1]?.url ?? image[0]?.url ?? "",
                        width: 220,
                        height: 220,
                      },
                      {
                        media: "(min-width: 1024px)",
                        src: image[1]?.url ?? image[0]?.url ?? "",
                        width: 424,
                        height: 424,
                      },
                    ]}
                    loading="eager"
                    fetchPriority="low"
                    decoding="async"
                    className="hidden lg:group-hover:flex absolute inset-0 w-full h-full object-cover"
                    src={image[1]?.url ?? image[0]?.url ?? ""}
                    alt={getProductImageAlt(productName, 1, selectedColor)}
                    width={220}
                    height={220}
                  />
                )}
              </div>
            )}
          </a>
          <div className="flex w-full items-center flex-col lg:group-hover:justify-end">
            <VariantSelectorShelf
              {...{
                product,
                listPrice,
                price,
                seller,
                setCurrentSku: (sku: string | number) =>
                  setCurrentSku(String(sku)),
              }}
            />
          </div>
        </div>
        <a
          href={sanitizeStoreUrl(availableVariant?.url || product.url)}
          tabIndex={-1}
          className="group flex flex-col items-center w-full"
        >
          {/* `h-[31px]` reserva espaço fixo pra até 2 linhas de nome; sem
              `flex flex-col justify-end`, nome de 1 linha fica ancorado no
              topo, sobrando espaço em branco antes do preço (gap "muito
              grande" reportado pelo time). Ancorar embaixo não muda a altura
              total nem a posição do preço abaixo. */}
          <p className="text-start mt-2.5 font-['Poppins'] not-italic font-normal text-xs leading-[15px] text-[#757575] lg:text-sm h-[31px] overflow-hidden w-full max-w-[400px] m-auto tracking-[0.06em] flex flex-col justify-end">
            {productName}
          </p>

          <div className="flex w-full items-start flex-col max-w-[400px] m-auto">
            <p className="listprice text-center font-['Poppins'] not-italic font-normal text-xs line-through text-[#757575] mb-1 h-4 mt-3">
              {listPrice !== price ? formatPrice(listPrice) : ""}
            </p>
            <p className="price text-start text-[#1A1A1A] font-['Poppins'] not-italic font-medium text-sm m-0">
              {formatPrice(price)}
            </p>
            <p className="text-[#434343] text-start font-['Poppins'] not-italic font-normal text-xs">
              {installments != null
                ? `ou ${installments}`
                : `ou 1x de ${price}`}
            </p>
          </div>
        </a>
      </div>
    </Slider.Item>
  );
};

export default ProductItemCategories;
